import type { ValidationErrors } from '@angular/forms';

/** Form error key storing the contextual phone pattern message id. */
export const ANC_PHONE_PATTERN_ERROR = 'ancPhonePattern';

/** Contextual phone pattern error keys (priority order). */
export type AncPhonePatternErrorKey =
    | 'empty'
    | 'invalidCharacter'
    | 'digitBeforeParentheses'
    | 'emptyParentheses'
    | 'multiplePlus'
    | 'plusAfterDigit'
    | 'noDigits'
    | 'exceedsMaxDigits'
    | 'default';

/** Default i18n strings for telephone pattern feedback. */
export const ANC_PHONE_PATTERN_ERROR_MESSAGES: Readonly<
    Record<AncPhonePatternErrorKey, string>
> = {
    empty: 'Contact Number cannot be empty',
    invalidCharacter: 'Invalid character found',
    digitBeforeParentheses: 'No number should appear before parentheses',
    emptyParentheses: 'Parentheses must contain digits',
    multiplePlus: 'Only one plus sign is allowed',
    plusAfterDigit: 'Plus sign must appear before the first digit',
    noDigits: 'No digits found',
    exceedsMaxDigits: 'Contact Number is too long (Maximum 15 digits are allowed)',
    default:
        'Contact Number allows only digits (0–9), plus (+), parentheses (), and spaces'
};

/** Whether the phone value is empty or whitespace only. */
export function isPhoneEmpty(value: string | null | undefined): boolean {
    return !value?.trim();
}

/** Whether the phone value contains characters outside the allowed set. */
export function hasInvalidPhoneCharacter(value: string | null | undefined): boolean {
    return /[^0-9+\s()-]/.test(value || '');
}

/** Whether more than one plus sign is present. */
export function hasMultiplePhonePlus(value: string | null | undefined): boolean {
    return (value?.match(/\+/g) || []).length > 1;
}

/**
 * Whether a plus sign appears after a digit anywhere in the value.
 * Matches the telephone regex negative lookahead `(?!.*\d.*\+)`.
 */
export function hasPhonePlusAfterDigit(value: string | null | undefined): boolean {
    return !!value && /\d.*\+/.test(value);
}

/** Whether the phone value contains no digits. */
export function hasNoPhoneDigits(value: string | null | undefined): boolean {
    return !/\d/.test(value || '');
}

/** Whether the phone value exceeds 15 digits. */
export function exceedsPhoneMaxDigits(value: string | null | undefined): boolean {
    return (value || '').replace(/\D/g, '').length > 15;
}

/** Whether the phone value has empty parentheses. */
export function hasEmptyPhoneParentheses(value: string | null | undefined): boolean {
    return /\(\s*\)/.test(value || '');
}

/** Whether any digit appears before an opening parenthesis. */
export function hasPhoneDigitBeforeParentheses(value: string | null | undefined): boolean {
    if (!value) {
        return false;
    }

    const indexOfParen = value.indexOf('(');
    if (indexOfParen === -1) {
        return false;
    }

    return /\d/.test(value.slice(0, indexOfParen));
}

/**
 * Resolves the contextual pattern error key for a phone value.
 * Matches the legacy ngSwitch priority used across order/shipment forms.
 */
export function getPhonePatternErrorKey(
    value: string | null | undefined
): AncPhonePatternErrorKey {
    if (isPhoneEmpty(value)) {
        return 'empty';
    }
    if (hasInvalidPhoneCharacter(value)) {
        return 'invalidCharacter';
    }
    if (hasPhoneDigitBeforeParentheses(value)) {
        return 'digitBeforeParentheses';
    }
    if (hasEmptyPhoneParentheses(value)) {
        return 'emptyParentheses';
    }
    if (hasMultiplePhonePlus(value)) {
        return 'multiplePlus';
    }
    if (hasPhonePlusAfterDigit(value)) {
        return 'plusAfterDigit';
    }
    if (hasNoPhoneDigits(value)) {
        return 'noDigits';
    }
    if (exceedsPhoneMaxDigits(value)) {
        return 'exceedsMaxDigits';
    }

    return 'default';
}

/**
 * Resolves the contextual pattern error key for the current phone value.
 * Always derives from the live value so UI matches what the user sees.
 */
export function resolvePhonePatternErrorKey(
    value: string | null | undefined,
    _errors: ValidationErrors | null | undefined = null
): AncPhonePatternErrorKey {
    return getPhonePatternErrorKey(value);
}

/** Merges contextual phone pattern metadata into existing validation errors. */
export function mergeAncPhonePatternErrors(
    errors: ValidationErrors | null,
    value: string | null | undefined
): ValidationErrors | null {
    if (!errors?.['pattern']) {
        return errors;
    }

    const normalized = value != null ? String(value) : '';
    return {
        ...errors,
        [ANC_PHONE_PATTERN_ERROR]: getPhonePatternErrorKey(normalized)
    };
}

/** Resolves the user-facing pattern error message for a phone value. */
export function getPhonePatternErrorMessage(
    value: string | null | undefined,
    fieldLabel = 'Contact Number',
    errors: ValidationErrors | null | undefined = null
): string {
    const message =
        ANC_PHONE_PATTERN_ERROR_MESSAGES[resolvePhonePatternErrorKey(value, errors)];
    return message.replace(/Contact Number/g, fieldLabel);
}
